import React, { useMemo } from 'react';
import { Alert, Box, TextField, Typography, type SxProps, type Theme } from '@mui/material';
import { FONT_ENUM } from '../../types/global/fonts.types';
import PictureUploader from '../PictureUploader/PictureUploader';
import type { TImage } from '../Feed/PhotoUploadModal';
import { formatManualEngagementRate, type IManualPlatformLinkData } from '../../utils/manualPlatformLinkUtils';
import { getDevManualPlatformLinkData } from '../../utils/devAutofill';
import { DevAutofillButton } from './DevAutofillButton';

export interface ManualPlatformLinkFormProps {
    value: IManualPlatformLinkData;
    onChange: (data: IManualPlatformLinkData) => void;
    /** Show banner when OAuth / auto-link is disabled */
    showAutoLinkUnavailableNotice?: boolean;
    usernameLabel?: string;
    followersLabel?: string;
    textFieldSx?: SxProps<Theme>;
    fieldLabelSx?: SxProps<Theme>;
    /** Optional theme token for notice / engagement panel */
    mutedColor?: string;
    highlightColor?: string;
    /** Pre-filled headshot when creator already has a profile picture */
    existingHeadshotUrl?: string;
}

const defaultFieldLabelSx: SxProps<Theme> = {
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: '0.15em',
    fontWeight: 700,
    mb: 1,
    ml: 0.5,
};

const defaultTextFieldSx: SxProps<Theme> = {
    '& .MuiOutlinedInput-root': {
        minHeight: 56,
        pl: 1,
        borderRadius: 3,
        '& fieldset': { border: 'none' },
    },
};

type LastPostField = 'lastPostViews' | 'lastPostComments' | 'lastPostLikes';

const LAST_POST_FIELDS: { field: LastPostField; label: string }[] = [
    { field: 'lastPostViews', label: 'Total views on your last post' },
    { field: 'lastPostComments', label: 'Total comments on your last post' },
    { field: 'lastPostLikes', label: 'Total likes on your last post' },
];

export function ManualPlatformLinkForm({
    value,
    onChange,
    showAutoLinkUnavailableNotice = false,
    usernameLabel = 'Username',
    followersLabel = 'Total followers',
    textFieldSx,
    fieldLabelSx,
    mutedColor = '#7a7a7a',
    highlightColor = '#ffc965',
    existingHeadshotUrl,
}: ManualPlatformLinkFormProps) {
    const labelSx = fieldLabelSx ?? defaultFieldLabelSx;
    const inputSx = textFieldSx ?? defaultTextFieldSx;

    const headshotPreview = value.headshotPreview !== undefined ? value.headshotPreview : existingHeadshotUrl;

    const engagementDisplay = useMemo(
        () => formatManualEngagementRate(value.followers, value.lastPostLikes, value.lastPostComments),
        [value.followers, value.lastPostLikes, value.lastPostComments],
    );

    const patch = (field: keyof IManualPlatformLinkData, raw: string) => {
        const sanitized = field === 'userName' ? raw : raw.replace(/[^\d]/g, '');
        onChange({ ...value, [field]: sanitized });
    };

    const handleHeadshotChange = (image: string | TImage[] | null, rawFile?: File[] | FileList | null) => {
        if (!image) {
            onChange({ ...value, headshotPreview: '', headshotFile: undefined });
            return;
        }
        const file = rawFile?.[0] instanceof File ? rawFile[0] : undefined;
        const preview = typeof image === 'string' ? image : undefined;
        onChange({
            ...value,
            headshotPreview: preview,
            headshotFile: file,
        });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {showAutoLinkUnavailableNotice && (
                        <Alert
                            severity="info"
                            sx={{
                                borderRadius: 2,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                '& .MuiAlert-message': { width: '100%' },
                            }}>
                            Automatic linking is not currently available. Enter your stats manually below — brands may
                            verify these numbers before approving your application.
                        </Alert>
                    )}
                </Box>
                <DevAutofillButton
                    onAutofill={() => onChange(getDevManualPlatformLinkData())}
                    title="Dev: fill manual Instagram stats"
                    color={highlightColor}
                />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <Typography sx={{ ...labelSx, ml: 0 }}>
                    Headshot{' '}
                    <Typography component="span" sx={{ color: '#f87171' }}>
                        *
                    </Typography>
                </Typography>
                <Typography sx={{ fontSize: 12, color: mutedColor, mb: 1.5, lineHeight: 1.5, maxWidth: 280 }}>
                    Upload a clear photo of yourself. Brands use this to verify your identity.
                </Typography>
                <PictureUploader
                    initialImageSrc={headshotPreview}
                    onChange={handleHeadshotChange}
                    onRemove={() => handleHeadshotChange(null)}
                    size={80}
                    aspect={1}
                    cropShape="round"
                    cropModalTitle="Crop headshot"
                />
            </Box>

            <Box>
                <Typography sx={labelSx}>{usernameLabel}</Typography>
                <TextField
                    fullWidth
                    placeholder="@yourhandle"
                    value={value.userName}
                    onChange={e => patch('userName', e.target.value.replace(/^@+/, ''))}
                    sx={inputSx}
                />
            </Box>

            <Box>
                <Typography sx={labelSx}>{followersLabel}</Typography>
                <TextField
                    fullWidth
                    placeholder="10000"
                    value={value.followers}
                    onChange={e => patch('followers', e.target.value)}
                    inputProps={{ inputMode: 'numeric' }}
                    sx={inputSx}
                />
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                    gap: 2,
                }}>
                {LAST_POST_FIELDS.map(({ field, label }) => (
                    <Box key={field}>
                        <Typography sx={labelSx}>{label}</Typography>
                        <TextField
                            fullWidth
                            placeholder="0"
                            value={value[field]}
                            onChange={e => patch(field, e.target.value)}
                            inputProps={{ inputMode: 'numeric' }}
                            sx={inputSx}
                        />
                    </Box>
                ))}
            </Box>

            <Box
                sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${highlightColor}44`,
                    bgcolor: `${highlightColor}0a`,
                }}>
                <Typography
                    sx={{
                        fontSize: 10,
                        textTransform: 'uppercase',
                        letterSpacing: '0.12em',
                        fontWeight: 700,
                        color: mutedColor,
                        mb: 0.5,
                    }}>
                    Calculated engagement rate
                </Typography>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 22,
                        color: highlightColor,
                        lineHeight: 1.2,
                    }}>
                    {engagementDisplay}
                </Typography>
                <Typography sx={{ fontSize: 12, color: mutedColor, mt: 1, lineHeight: 1.5 }}>
                    Based on (likes + comments) ÷ followers on your last post. Enter accurate numbers — brands may
                    cross-check your profile before approving you.
                </Typography>
            </Box>
        </Box>
    );
}
